import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { pb } from '@/lib/pocketbase'
import type { RfqForm, RfqFormItem } from '@/lib/pocketbase'

function normalizeRfqForm(form: any): RfqForm {
  return {
    ...form,
    tender_id: form.tender || form.tender_id,
    budget_amount: form.budget_amount ?? form.budget_amount_satang,
    ref_amount: form.ref_amount ?? form.ref_amount_satang,
    expand: { ...(form as any).expand, tender_id: (form as any).expand?.tender },
  }
}

function normalizeRfqItems(items: any[]): RfqFormItem[] {
  return items.map((item, index) => ({
    ...item,
    form_id: item.form_id || item.rfq_form,
    line_number: item.line_number ?? index + 1,
    quantity: item.quantity ?? item.qty,
    unit_price: item.unit_price ?? item.unit_price_satang,
    line_total: item.line_total ?? item.line_total_satang,
  }))
}

interface GenerateTenderRfqPayload {
  tenderId: string
  model?: string
  force?: boolean
  maxAttachments?: number
  attachmentIds?: string[]
}

interface GenerateTenderRfqResponse {
  tenderId: string
  rfqFormId: string
  model: string
  itemCount: number
  usedAttachmentCount: number
}

// Fetch RFQ form for a tender
export function useTenderRfq(tenderId: string) {
  return useQuery({
    queryKey: ['tender-rfq', tenderId],
    queryFn: async () => {
      try {
        const rfqForm = await pb.collection('rfq_forms').getFirstListItem<RfqForm>(
          `tender = "${tenderId}"`
        )

        // Get items
        const items = await pb.collection('rfq_form_items').getList<RfqFormItem>(1, 100, {
          filter: `rfq_form = "${rfqForm.id}"`,
          sort: 'created',
        })

        return {
          form: normalizeRfqForm(rfqForm as any),
          items: normalizeRfqItems(items.items as any[]),
        }
      } catch {
        return null
      }
    },
    enabled: !!tenderId,
  })
}

// Fetch all RFQ forms (Pro ops)
export function useRfqForms(filters?: {
  search?: string
  page?: number
  perPage?: number
}) {
  const { search, page = 1, perPage = 20 } = filters || {}

  return useQuery({
    queryKey: ['rfq-forms', filters],
    queryFn: async () => {
      let filter = ''
      if (search) {
        filter = `tender.title_th ~ "${search}" || tender.egp_project_no ~ "${search}"`
      }

      const result = await pb.collection('rfq_forms').getList<RfqForm>(page, perPage, {
        filter,
        expand: 'tender',
        sort: '-request_date',
      })
      return {
        ...result,
        items: result.items.map((item) => normalizeRfqForm(item as any)),
      }
    },
  })
}

// Fetch RFQ form items
export function useRfqFormItems(formId: string) {
  return useQuery({
    queryKey: ['rfq-form-items', formId],
    queryFn: async () => {
      const result = await pb.collection('rfq_form_items').getList<RfqFormItem>(1, 500, {
        filter: `rfq_form = "${formId}"`,
        sort: 'created',
      })
      return normalizeRfqItems(result.items as any[])
    },
    enabled: !!formId,
  })
}

// Generate RFQ on-demand for a specific tender
export function useGenerateTenderRfq() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: GenerateTenderRfqPayload) => {
      if (!payload.tenderId) {
        throw new Error('tenderId is required')
      }

      return pb.send<GenerateTenderRfqResponse>('/api/rfq/generate', {
        method: 'POST',
        body: payload,
      })
    },
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['tender-rfq', variables.tenderId],
        }),
        queryClient.invalidateQueries({ queryKey: ['rfq-forms'] }),
        queryClient.invalidateQueries({ queryKey: ['rfq-form-items'] }),
      ])
    },
  })
}
